import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography,
    IconButton,
    Box,
    Link,
    Tooltip,
    Snackbar,
    Alert,
    Chip,
    Stack,
    Card,
    CardContent
} from '@mui/material';
import { 
    ContentCopy, 
    Edit, 
    Delete, 
    Message, 
    Schedule, 
    People,
    CheckCircle,
    Link as LinkIcon
} from '@mui/icons-material';
import DeleteBarDialog from './DeleteBarDialog';
import EditBarDialog from './EditBarDialog';

export default function BarList() {
    const [bars, setBars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [barToDelete, setBarToDelete] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [barToEdit, setBarToEdit] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchBars();
    }, []);

    const fetchBars = async () => {
        try {
            // First, fetch the bars
            const response = await api.get('/api/admin/bars');
            
            // For each bar, fetch and update its message stats
            const updatedBars = await Promise.all(response.map(async (bar) => {
                try {
                    // Call the endpoint with the bar's ID using the correct URL
                    const messageStats = await api.get(`/api/users/count-message-blasts-all/${bar.id}`);
                    return {
                        ...bar,
                        messageStats: messageStats?.data || {
                            totalUsers: 0,
                            messagesBlastSent: 0,
                            messagesPending: 0,
                            confirmedUsers: 0,
                            lastUpdated: new Date()
                        }
                    };
                } catch (statError) {
                    console.error(`Error fetching stats for bar ${bar.id}:`, statError);
                    // Return the bar with default message stats if there's an error
                    return {
                        ...bar,
                        messageStats: {
                            totalUsers: 0,
                            messagesBlastSent: 0,
                            messagesPending: 0,
                            confirmedUsers: 0,
                            lastUpdated: new Date()
                        }
                    };
                }
            }));
            
            setBars(updatedBars);
            setError(null);
        } catch (err) {
            console.error('Error fetching bars:', err);
            setError('Failed to load bars');
            showSnackbar('Failed to load bars', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (bar) => {
        setBarToDelete(bar);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setLoading(true);
            await api.delete(`/api/admin/bars/${barToDelete.id}`);
            await fetchBars();
            setDeleteDialogOpen(false);
            setBarToDelete(null);
            showSnackbar(`Successfully deleted ${barToDelete.barName}`, 'success');
        } catch (error) {
            console.error('Error deleting bar:', error);
            showSnackbar(`Failed to delete ${barToDelete.barName}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (bar) => {
        setBarToEdit(bar);
        setEditDialogOpen(true);
    };

    const handleEditConfirm = async (updatedData) => {
        try {
            setLoading(true);
            // Make the API call to update the bar
            await api.put(`/api/admin/bars/${barToEdit.id}`, updatedData);
            
            // Fetch the updated data to ensure we have the latest state
            await fetchBars();
            
            setEditDialogOpen(false);
            setBarToEdit(null);
            showSnackbar('Bar updated successfully', 'success');
        } catch (error) {
            console.error('Error updating bar:', error);
            showSnackbar(error.message || 'Failed to update bar', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getSignupUrl = (bar) => {
        // Use the bar's slug, or generate one from the name if no slug exists
        const slug = bar.slug || (bar.barName ? bar.barName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : bar.id);
        return `${window.location.origin}/join/${slug}`;
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showSnackbar('URL copied to clipboard', 'success');
        } catch (err) {
            showSnackbar('Failed to copy URL', 'error');
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading && bars.length === 0) return <Typography>Loading...</Typography>;
    if (error && bars.length === 0) return <Typography color="error">{error}</Typography>;

    return (
        <Box sx={{ mt: 4, pb: 8 }}>
            <Card elevation={2}>
                <CardContent>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                        Bar Management
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%" sx={{ fontWeight: 'bold' }}>Bar Details</TableCell>
                                    <TableCell width="20%" sx={{ fontWeight: 'bold' }}>Contact Info</TableCell>
                                    <TableCell width="15%" align="center" sx={{ fontWeight: 'bold' }}>Users</TableCell>
                                    <TableCell width="25%" align="center" sx={{ fontWeight: 'bold' }}>Message Stats</TableCell>
                                    <TableCell width="20%" align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bars.map((bar) => (
                                    <TableRow key={bar.id} hover>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    {bar.barName || 'Unnamed Bar'}
                                                </Typography>
                                                <Tooltip title="Copy Signup URL">
                                                    <Chip
                                                        icon={<LinkIcon fontSize="small" />}
                                                        label="Copy Signup Link"
                                                        size="small"
                                                        onClick={() => copyToClipboard(getSignupUrl(bar))}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {bar.email || 'No email'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {bar.phoneNumber || 'No phone'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack spacing={1} alignItems="center">
                                                <Chip
                                                    icon={<People fontSize="small" />}
                                                    label={`${bar.userCount || 0} total`}
                                                    size="small"
                                                    color="primary"
                                                />
                                                <Chip
                                                    icon={<CheckCircle fontSize="small" />}
                                                    label={`${bar.messageStats?.confirmedUsers || 0} confirmed`}
                                                    size="small"
                                                    color="success"
                                                />
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack spacing={1} alignItems="center">
                                                <Chip
                                                    icon={<Message fontSize="small" />}
                                                    label={`${bar.messageStats?.messagesBlastSent || 0} sent`}
                                                    size="small"
                                                    color="primary"
                                                />
                                                {bar.messageStats?.messagesPending > 0 && (
                                                    <Chip
                                                        icon={<Schedule fontSize="small" />}
                                                        label={`${bar.messageStats?.messagesPending} pending`}
                                                        size="small"
                                                        color="warning"
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Tooltip title="Edit Bar">
                                                    <IconButton 
                                                        size="small"
                                                        onClick={() => handleEditClick(bar)}
                                                        disabled={loading}
                                                        color="primary"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Bar">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleDeleteClick(bar)}
                                                        disabled={loading}
                                                        color="error"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {bars.length === 0 && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No bars found. Add a new bar using the Onboard New Bar tab.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <DeleteBarDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setBarToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                bar={barToDelete}
            />
            
            <EditBarDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setBarToEdit(null);
                }}
                onConfirm={handleEditConfirm}
                bar={barToEdit}
            />

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    variant="filled"
                    elevation={6}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
} 